import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    type ReactNode,
} from 'react';

/**
 * VoicePlaybackQueue — serialises auto-play across multiple voice-message
 * bubbles in one conversation.
 *
 * The bot can emit bursts of 2–3 short replies (L3/L4 familiarity), each
 * persisted as a separate message. Without a queue, every bubble's
 * auto-play effect fires independently and the MP3s overlap. With this
 * queue:
 *
 *   1. A bubble whose `autoPlay` prop is true calls `enqueueAutoplay()`
 *      with its message id + a `play()` thunk.
 *   2. If nothing is playing, the thunk fires immediately and the queue
 *      tracks it as `playingId`.
 *   3. The bubble listens for the audio element's `ended` event and calls
 *      `releaseLock()`, which pops the next queued entry and plays it.
 *   4. Bubble unmount also releases the lock (user navigated away).
 *
 * Manual play / pause does NOT go through the queue — user-initiated
 * playback bypasses sequencing.
 */

interface QueueEntry {
    messageId: string;
    /** Thunk that starts the audio element. May reject (autoplay blocked). */
    play: () => Promise<void>;
}

interface VoicePlaybackQueueValue {
    /** Register an auto-play request. Idempotent per messageId. */
    enqueueAutoplay: (entry: QueueEntry) => void;
    /** Tell the queue the message just finished (or was unmounted/cancelled). */
    releaseLock: (messageId: string) => void;
}

const Ctx = createContext<VoicePlaybackQueueValue | null>(null);

export function VoicePlaybackQueueProvider({ children }: { children: ReactNode }) {
    const queueRef = useRef<QueueEntry[]>([]);
    const playingIdRef = useRef<string | null>(null);

    const tryNext = useCallback(() => {
        if (playingIdRef.current !== null) return;
        const next = queueRef.current.shift();
        if (!next) return;
        playingIdRef.current = next.messageId;
        next.play().catch(() => {
            // Browser blocked autoplay (no prior user gesture) or the
            // audio element was unmounted between enqueue and play. Skip
            // and try the next one rather than blocking the whole queue.
            if (playingIdRef.current === next.messageId) {
                playingIdRef.current = null;
                tryNext();
            }
        });
    }, []);

    const enqueueAutoplay = useCallback(
        (entry: QueueEntry) => {
            if (playingIdRef.current === entry.messageId) return;
            if (queueRef.current.some((e) => e.messageId === entry.messageId)) return;
            queueRef.current.push(entry);
            tryNext();
        },
        [tryNext],
    );

    const releaseLock = useCallback(
        (messageId: string) => {
            // Drop a pending entry for this id (e.g. unmounted before its turn).
            queueRef.current = queueRef.current.filter((e) => e.messageId !== messageId);
            if (playingIdRef.current === messageId) {
                playingIdRef.current = null;
                tryNext();
            }
        },
        [tryNext],
    );

    // CRITICAL: memoise the context value. Without this the Provider hands
    // out a fresh `{enqueueAutoplay, releaseLock}` object every render, which
    // changes the `queue` reference that VoiceMessageBubble depends on in
    // its autoplay useEffect. The effect then re-fires → cleanup runs
    // `releaseLock` for the CURRENTLY PLAYING message → queue immediately
    // pops the next entry while the previous audio is still playing → the
    // two clips overlap. Stable identity here is what serialises them.
    const value = useMemo(
        () => ({ enqueueAutoplay, releaseLock }),
        [enqueueAutoplay, releaseLock],
    );

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useVoicePlaybackQueue(): VoicePlaybackQueueValue | null {
    return useContext(Ctx);
}
